/**
 * Game modes + Adventure-mode level configuration.
 *
 * Locked spec (from design phase):
 *   - Daily: 1 random-seeded level (seed = UTC date), 1 life, scoring + share
 *   - Adventure: 7 discrete levels, 3 lives total, faster scroll/spiders + more
 *     hazards each level. High score persists (step 8).
 *   - Quick: 1 random level, 1 life, no persistence.
 *   - Practice: fixed onboarding seed, no persistence, gentler tuning. For
 *     new players to learn the rules without their record being affected.
 */

export type Mode = 'daily' | 'adventure' | 'quick' | 'practice';

export const TOTAL_ADVENTURE_LEVELS = 7;
export const ADVENTURE_LIVES = 3;

/**
 * Fixed seed for Practice mode. Picked deliberately to produce a playfield
 * with a manageable mix of features — enough water and spiders to be
 * recognizable as the real game, but generous space around the spawn. The
 * same seed for everyone means tutorial hints can refer to specific tiles.
 */
export const PRACTICE_SEED = 'practice-001';

/**
 * Tuning multipliers for Practice mode. Gentler than Level 1 of Adventure
 * so the player can actually read the playfield before the scroll starts
 * pushing them around.
 */
export const PRACTICE_TUNING: LevelConfig = {
  scrollIntervalMul: 1.35,    // 35% slower scroll
  spiderTickMul: 1.3,         // 30% slower spider patrols
  waterMultiplier: 0.6,       // fewer water hazards
};

/**
 * Per-level multipliers applied on top of the base scroll/spider tuning.
 *   scrollSpeedMul:  multiplies INTERVAL_MIN_MS (lower = faster scroll cap)
 *                    --- WAIT: lower interval = faster scroll. We want LATER
 *                    levels to be FASTER, so multiplier should be < 1.
 *                    Index 0 (level 1) = 1.0, index 6 (level 7) = ~0.55.
 *   spiderTickMul:   multiplies DEFAULT_TICK_INTERVAL_MS (lower = faster)
 *   waterMultiplier: multiplies water cluster count (more puddles)
 *
 * Hand-tuned so each step feels meaningfully harder without a cliff.
 */
export interface LevelConfig {
  scrollIntervalMul: number;
  spiderTickMul: number;
  waterMultiplier: number;
}

const LEVELS: LevelConfig[] = [
  { scrollIntervalMul: 1.0,  spiderTickMul: 1.0,  waterMultiplier: 1.0 },
  { scrollIntervalMul: 0.9,  spiderTickMul: 0.92, waterMultiplier: 1.1 },
  { scrollIntervalMul: 0.82, spiderTickMul: 0.85, waterMultiplier: 1.2 },
  { scrollIntervalMul: 0.75, spiderTickMul: 0.78, waterMultiplier: 1.3 },
  { scrollIntervalMul: 0.68, spiderTickMul: 0.72, waterMultiplier: 1.4 },
  { scrollIntervalMul: 0.62, spiderTickMul: 0.66, waterMultiplier: 1.55 },
  { scrollIntervalMul: 0.55, spiderTickMul: 0.6,  waterMultiplier: 1.7 },
];

/** Get config for 1-indexed level number (1..TOTAL_ADVENTURE_LEVELS). */
export function levelConfig(level: number): LevelConfig {
  const i = Math.max(0, Math.min(LEVELS.length - 1, level - 1));
  return LEVELS[i]!;
}

export function modeLabel(m: Mode): string {
  switch (m) {
    case 'daily': return 'DAILY';
    case 'adventure': return 'ADVENTURE';
    case 'quick': return 'QUICK PLAY';
    case 'practice': return 'PRACTICE';
  }
}

export function modeDescription(m: Mode): string {
  switch (m) {
    case 'daily': return 'one playfield, one life,\nsame for everyone today';
    case 'adventure': return '7 levels, 3 lives,\nfaster every level';
    case 'quick': return 'random playfield, one life,\nnothing on the line';
    case 'practice': return 'learn the rules,\nnothing recorded';
  }
}
